/**
 * Custom Private vault card fields for CredentialVault__requestLogin.
 * Defaults to username/password; models pass fields when they already know
 * the shape (Key ID / Issuer ID / .p8 upload, API tokens, etc.).
 */

export const VAULT_REQUEST_FIELD_TYPES = [
  'text',
  'email',
  'password',
  'secret',
  'file'
] as const

export type VaultRequestFieldType = (typeof VAULT_REQUEST_FIELD_TYPES)[number]

export type VaultRequestField = {
  /** Stable key stored in the vault JSON value. */
  id: string
  label: string
  type: VaultRequestFieldType
  required?: boolean
  placeholder?: string
  /** For type=file — e.g. ".p8,.pem,text/plain" */
  accept?: string
}

export const DEFAULT_LOGIN_VAULT_FIELDS: VaultRequestField[] = [
  {
    id: 'username',
    label: 'Username or email',
    type: 'email',
    required: true,
    placeholder: 'Username or email'
  },
  {
    id: 'password',
    label: 'Password',
    type: 'password',
    required: true,
    placeholder: 'Password'
  }
]

const MAX_FIELDS = 12
const MAX_ID_LEN = 64
const MAX_LABEL_LEN = 80
const MAX_PLACEHOLDER_LEN = 120
const MAX_ACCEPT_LEN = 120
const MAX_FILE_CHARS = 80_000

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function sanitizeId(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, MAX_ID_LEN)
}

function isFieldType(value: unknown): value is VaultRequestFieldType {
  return (
    typeof value === 'string' &&
    (VAULT_REQUEST_FIELD_TYPES as readonly string[]).includes(value)
  )
}

/**
 * Normalize model-supplied fields. Falls back to username/password login
 * when omitted or empty.
 */
export function normalizeVaultRequestFields(
  raw: unknown
): VaultRequestField[] {
  if (!Array.isArray(raw) || raw.length === 0) {
    return DEFAULT_LOGIN_VAULT_FIELDS.map(field => ({ ...field }))
  }

  const seen = new Set<string>()
  const fields: VaultRequestField[] = []

  for (const item of raw.slice(0, MAX_FIELDS)) {
    const record = asRecord(item)
    if (!record) continue

    const id = sanitizeId(
      typeof record.id === 'string'
        ? record.id
        : typeof record.key === 'string'
          ? record.key
          : typeof record.name === 'string'
            ? record.name
            : ''
    )
    if (!id || seen.has(id)) continue

    const label =
      (typeof record.label === 'string' && record.label.trim()) ||
      (typeof record.title === 'string' && record.title.trim()) ||
      id
    const type: VaultRequestFieldType = isFieldType(record.type)
      ? record.type
      : id.includes('password') || id.includes('secret') || id.includes('token')
        ? 'secret'
        : id.includes('email') || id.includes('user')
          ? 'email'
          : id.includes('file') || id.includes('p8') || id.includes('pem') || id.includes('key_file')
            ? 'file'
            : 'text'

    const field: VaultRequestField = {
      id,
      label: label.slice(0, MAX_LABEL_LEN),
      type,
      required: record.required !== false
    }

    if (typeof record.placeholder === 'string' && record.placeholder.trim()) {
      field.placeholder = record.placeholder.trim().slice(0, MAX_PLACEHOLDER_LEN)
    }
    if (typeof record.accept === 'string' && record.accept.trim()) {
      field.accept = record.accept.trim().slice(0, MAX_ACCEPT_LEN)
    } else if (type === 'file') {
      field.accept = '.p8,.pem,.key,text/plain,application/x-pem-file'
    }

    seen.add(id)
    fields.push(field)
  }

  return fields.length > 0
    ? fields
    : DEFAULT_LOGIN_VAULT_FIELDS.map(field => ({ ...field }))
}

export function isDefaultLoginFields(fields: VaultRequestField[]): boolean {
  if (fields.length !== 2) return false
  return (
    fields[0]?.id === 'username' &&
    fields[0]?.type === 'email' &&
    fields[1]?.id === 'password' &&
    fields[1]?.type === 'password'
  )
}

export function inferVaultCredentialKind(
  fields: VaultRequestField[]
): 'password' | 'api_key' | 'custom' {
  if (isDefaultLoginFields(fields)) return 'password'
  if (
    fields.some(
      field =>
        field.type === 'file' ||
        /api|token|issuer|key_id|p8|pem|secret/i.test(field.id)
    )
  ) {
    return 'api_key'
  }
  return 'custom'
}

export function buildVaultSecretValueFromFields(
  values: Record<string, string>
): string {
  const cleaned: Record<string, string> = {}
  for (const [key, value] of Object.entries(values)) {
    const trimmed = value.trim()
    if (trimmed) cleaned[key] = trimmed
  }
  return JSON.stringify(cleaned)
}

export function readVaultUploadAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (file.size > MAX_FILE_CHARS) {
      reject(new Error('File is too large for Private vault (max ~80KB)'))
      return
    }
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Could not read that file'))
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : ''
      if (!text.trim()) {
        reject(new Error('File was empty'))
        return
      }
      if (text.length > MAX_FILE_CHARS) {
        reject(new Error('File contents are too large for Private vault'))
        return
      }
      resolve(text)
    }
    reader.readAsText(file)
  })
}
